import { skipToken } from '@reduxjs/toolkit/query';
import type { AssetMetricPoint, ExchangeAssetMetricPoint } from '@src/providers/CoinMetricsApiProvider';
import {
  isForbiddenCoinMetricsError,
  useCoinMetricsApiKey,
  useGetAssetMetricsQuery,
  useGetExchangeAssetMetricsQuery,
} from '@src/providers/CoinMetricsApiProvider';
import { createDateOnlyFromDate } from '@talos/kyoko';
import type { SeriesLineOptions } from 'highcharts';
import { useMemo } from 'react';
import { CM_MAX_PAGE_SIZE, exchangeFromPair, fundingRateFractionToPercent, useExchangeAssetCatalog } from '../core';
import type { FundingRateFrequency, FundingRateScope, FundingRateTimeRange } from './HistoricalFundingRate.types';
import {
  FUNDING_RATE_AGGREGATED_SERIES_NAME,
  FUNDING_RATE_MAX_EXCHANGES,
  FUNDING_RATE_METRIC,
  FUNDING_RATE_PRICE_METRIC,
  FUNDING_RATE_TIME_RANGE_DAYS,
} from './tokens';

interface UseHistoricalFundingRateDataParams {
  /** Resolved CoinMetrics asset ID (e.g. 'btc'). Query is skipped when undefined. */
  cmAssetId: string | undefined;
  /** Display name for the price overlay series (e.g. 'BTC'). */
  assetDisplayName: string | undefined;
  scope: FundingRateScope;
  frequency: FundingRateFrequency;
  timeRange: FundingRateTimeRange;
  /** User's persisted exchange selection (By Exchange scope). `undefined` (never configured) defaults
   * to the first FUNDING_RATE_MAX_EXCHANGES available; an explicit `[]` means the user cleared the
   * picker and is honored as "none". Only the effective exchanges are fetched — not the full catalog. */
  selectedExchanges?: string[];
}

interface UseHistoricalFundingRateDataResult {
  /** One series per selected exchange (exchange-asset scope) or a single aggregated series (asset-level). No colors assigned — caller handles color. */
  fundingRateSeries: SeriesLineOptions[];
  /** Single price overlay series for the RHS axis. */
  priceSeries: SeriesLineOptions[];
  /** Exchange names available for the current asset in By Exchange scope (e.g. ['binance','bybit']). Empty in asset-level scope. */
  availableExchanges: string[];
  /** Effective exchanges actually fetched/shown — the persisted selection (minus any no longer available),
   * the default first FUNDING_RATE_MAX_EXCHANGES when never configured, or empty when explicitly cleared. */
  selectedExchanges: string[];
  /** Earliest funding-rate timestamp — used to trim the price overlay so the chart starts where
   * funding data begins rather than at the (much earlier) start of price history. Undefined when no data. */
  fundingStartTime: number | undefined;
  isFetching: boolean;
  isError: boolean;
  /**
   * True when the price overlay (`FUNDING_RATE_PRICE_METRIC`) failed with a CoinMetrics 403
   * entitlement error — the key isn't entitled to price data, distinct from other causes folded
   * into `isError`. Surfaced separately so the caller can name it in a permission message instead
   * of the generic "failed to load" one.
   */
  isPriceForbidden: boolean;
  /** True when any active query hit the pagination cap, so the charted series is missing its oldest data. */
  isTruncated: boolean;
}

const EMPTY_SERIES: SeriesLineOptions[] = [];
const EMPTY_CM_ASSET_IDS: string[] = [];
const EMPTY_EXCHANGES: string[] = [];

/** Returns 'YYYY-MM-DD' for `days` days ago, or undefined when the range is All (no start filter). */
function getStartTime(timeRange: FundingRateTimeRange): string | undefined {
  const days = FUNDING_RATE_TIME_RANGE_DAYS[timeRange];
  if (days === null) {
    return undefined;
  }
  const d = new Date();
  d.setDate(d.getDate() - days);
  // Build the date-only string from local calendar components. Using toISOString() here would
  // convert to UTC and could shift the day by ±1 in non-UTC timezones.
  return createDateOnlyFromDate(d);
}

/**
 * Groups a flat exchange-asset-metrics response into one SeriesLineOptions per exchange-asset,
 * naming each series by its bare exchange id (e.g. `binance`, not `binance-btc`). Exported for
 * unit testing.
 */
export function buildExchangeAssetFundingRateSeries(
  points: ExchangeAssetMetricPoint[] | undefined,
  cmAssetId: string
): SeriesLineOptions[] {
  if (!points || points.length === 0) {
    return EMPTY_SERIES;
  }

  const byExchangeAsset = new Map<string, [number, number][]>();
  for (const point of points) {
    const rawValue = point[FUNDING_RATE_METRIC];
    if (rawValue == null) {
      continue;
    }
    const value = Number(rawValue);
    if (!Number.isFinite(value)) {
      continue;
    }
    const timestamp = new Date(point.time).getTime();
    if (!Number.isFinite(timestamp)) {
      continue;
    }

    if (!byExchangeAsset.has(point.exchange_asset)) {
      byExchangeAsset.set(point.exchange_asset, []);
    }
    byExchangeAsset.get(point.exchange_asset)!.push([timestamp, value]);
  }

  return Array.from(byExchangeAsset.entries())
    .toSorted(([a], [b]) => a.localeCompare(b))
    .map(([exchangeAsset, data]) => ({
      type: 'line' as const,
      name: exchangeFromPair(exchangeAsset, cmAssetId),
      // Highcharts requires ascending x; sort defensively in case the API returns points unordered.
      data: data.toSorted((a, b) => a[0] - b[0]),
    }));
}

/**
 * Extracts a single `[timestamp, value][]` series from an asset-metrics response.
 * Exported for unit testing.
 */
export function buildAssetMetricPoints(points: AssetMetricPoint[] | undefined, metric: string): [number, number][] {
  if (!points) {
    return [];
  }
  const data: [number, number][] = [];
  for (const point of points) {
    const rawValue = point[metric];
    if (rawValue == null) {
      continue;
    }
    const value = Number(rawValue);
    if (!Number.isFinite(value)) {
      continue;
    }
    const timestamp = new Date(point.time).getTime();
    if (!Number.isFinite(timestamp)) {
      continue;
    }
    data.push([timestamp, value]);
  }
  // Highcharts requires ascending x; sort defensively in case the API returns points unordered.
  return data.toSorted((a, b) => a[0] - b[0]);
}

/**
 * CoinMetrics reports the aggregate funding rate as a dimensionless fraction (e.g. 0.004872).
 * The chart axis and legend display it as a percent, so scale by 100 (0.004872 → 0.4872%).
 * Exported for unit testing.
 */
export function scaleFundingRateToPercent(data: [number, number][]): [number, number][] {
  return data.map(([timestamp, value]) => [timestamp, fundingRateFractionToPercent(value)]);
}

/**
 * Earliest timestamp (x) across the given series. Assumes each series' data is sorted ascending
 * (the builders above guarantee this), so only the first point of each series is inspected.
 * Exported for unit testing.
 */
export function earliestTimestamp(series: SeriesLineOptions[]): number | undefined {
  let earliest: number | undefined;
  for (const s of series) {
    const first = (s.data as [number, number][] | undefined)?.[0]?.[0];
    if (typeof first === 'number' && (earliest === undefined || first < earliest)) {
      earliest = first;
    }
  }
  return earliest;
}

/**
 * Resolves which exchanges to actually fetch/show in By Exchange scope: the user's persisted
 * selection with any no-longer-available exchanges dropped (e.g. after switching asset).
 *
 * Distinguishes three cases so the "Clear" action actually clears:
 * - `undefined` (never configured) → default to the first `max` available.
 * - `[]` (explicitly cleared by the user) → honor as "none selected"; do NOT snap back to defaults.
 * - non-empty but fully stale (e.g. after switching asset) → fall back to the default so the chart
 *   isn't unexpectedly blank.
 * Exported for unit testing.
 */
export function resolveSelectedExchanges(selected: string[] | undefined, available: string[], max: number): string[] {
  if (available.length === 0) {
    return EMPTY_EXCHANGES;
  }
  if (selected == null) {
    return available.slice(0, max);
  }
  if (selected.length === 0) {
    return EMPTY_EXCHANGES;
  }
  const valid = selected.filter(ex => available.includes(ex));
  return (valid.length > 0 ? valid : available).slice(0, max);
}

export function useHistoricalFundingRateData({
  cmAssetId,
  assetDisplayName,
  scope,
  frequency,
  timeRange,
  selectedExchanges,
}: UseHistoricalFundingRateDataParams): UseHistoricalFundingRateDataResult {
  const apiKey = useCoinMetricsApiKey();

  const startTime = useMemo(() => getStartTime(timeRange), [timeRange]);

  // ── Exchange-asset scope ──────────────────────────────────────────────────────

  // Memoize to avoid a new array reference every render (useExchangeAssetCatalog requires stability).
  const cmAssetIdsForCatalog = useMemo(
    () => (scope === 'exchange-asset' && cmAssetId ? [cmAssetId] : EMPTY_CM_ASSET_IDS),
    [scope, cmAssetId]
  );

  const {
    exchangeAssets,
    isLoading: isCatalogLoading,
    isError: isCatalogError,
    isTruncated: isCatalogTruncated,
  } = useExchangeAssetCatalog({ cmAssetIds: cmAssetIdsForCatalog, metric: FUNDING_RATE_METRIC });

  // Plain exchange names (e.g. 'binance') available for this asset, derived from the catalog's
  // exchange_asset strings (e.g. 'binance-btc'). Feeds both the picker and the effective selection.
  const availableExchanges = useMemo<string[]>(() => {
    if (scope !== 'exchange-asset' || !cmAssetId || exchangeAssets.length === 0) {
      return EMPTY_EXCHANGES;
    }
    return exchangeAssets.map(ea => exchangeFromPair(ea, cmAssetId));
  }, [scope, cmAssetId, exchangeAssets]);

  const effectiveExchanges = useMemo(
    () => resolveSelectedExchanges(selectedExchanges, availableExchanges, FUNDING_RATE_MAX_EXCHANGES),
    [selectedExchanges, availableExchanges]
  );

  // Fetch only the selected exchanges (not the whole catalog): smaller payload and avoids blowing
  // the endpoint's page cap, which would silently truncate history. Sorted for a stable cache key.
  const exchangeAssetsParam = useMemo(
    () =>
      effectiveExchanges
        .map(ex => `${ex}-${cmAssetId}`)
        .toSorted()
        .join(','),
    [effectiveExchanges, cmAssetId]
  );

  const exchangeAssetQueryArg =
    scope !== 'exchange-asset' || !apiKey || effectiveExchanges.length === 0
      ? skipToken
      : {
          exchange_assets: exchangeAssetsParam,
          metrics: FUNDING_RATE_METRIC,
          frequency,
          ...(startTime && { start_time: startTime }),
          page_size: CM_MAX_PAGE_SIZE,
          paging_from: 'end' as const,
        };

  const {
    currentData: exchangeAssetData,
    isFetching: isExchangeAssetFetching,
    isError: isExchangeAssetError,
  } = useGetExchangeAssetMetricsQuery(exchangeAssetQueryArg);

  // ── Asset-level scope ─────────────────────────────────────────────────────────

  const assetLevelQueryArg =
    scope !== 'asset-level' || !apiKey || !cmAssetId
      ? skipToken
      : {
          assets: cmAssetId,
          metrics: FUNDING_RATE_METRIC,
          frequency,
          ...(startTime && { start_time: startTime }),
          page_size: CM_MAX_PAGE_SIZE,
          paging_from: 'end' as const,
        };

  const {
    currentData: assetLevelData,
    isFetching: isAssetLevelFetching,
    isError: isAssetLevelError,
  } = useGetAssetMetricsQuery(assetLevelQueryArg);

  // ── Price overlay (always asset-level) ────────────────────────────────────────

  const priceQueryArg =
    !apiKey || !cmAssetId
      ? skipToken
      : {
          assets: cmAssetId,
          metrics: FUNDING_RATE_PRICE_METRIC,
          frequency,
          ...(startTime && { start_time: startTime }),
          page_size: CM_MAX_PAGE_SIZE,
          paging_from: 'end' as const,
        };

  const {
    currentData: priceData,
    isFetching: isPriceFetching,
    isError: isPriceError,
    error: priceError,
  } = useGetAssetMetricsQuery(priceQueryArg);

  // The price overlay has no catalog-based eligibility pre-check of its own (unlike the funding-rate
  // fetch, gated by useExchangeAssetCatalog / the module's catalog-based missing-metrics check) — it
  // always fires directly for the selected asset, so a per-metric entitlement gap on it can only be
  // detected from the query's own error, not from a silently-empty catalog.
  const isPriceForbidden = isForbiddenCoinMetricsError(priceError);

  // ── Series construction ───────────────────────────────────────────────────────

  const fundingRateSeries = useMemo<SeriesLineOptions[]>(() => {
    if (scope === 'exchange-asset') {
      return buildExchangeAssetFundingRateSeries(exchangeAssetData?.data, cmAssetId ?? '').map(s => ({
        ...s,
        data: scaleFundingRateToPercent(s.data as [number, number][]),
      }));
    }
    const data = scaleFundingRateToPercent(buildAssetMetricPoints(assetLevelData?.data, FUNDING_RATE_METRIC));
    if (data.length === 0) {
      return EMPTY_SERIES;
    }
    return [{ type: 'line', name: FUNDING_RATE_AGGREGATED_SERIES_NAME, data }];
  }, [scope, exchangeAssetData, assetLevelData, cmAssetId]);

  const priceSeries = useMemo<SeriesLineOptions[]>(() => {
    const data = buildAssetMetricPoints(priceData?.data, FUNDING_RATE_PRICE_METRIC);
    if (data.length === 0) {
      return EMPTY_SERIES;
    }
    return [{ type: 'line', name: assetDisplayName ?? cmAssetId ?? '', data }];
  }, [priceData, assetDisplayName, cmAssetId]);

  const isFetching =
    isPriceFetching ||
    (scope === 'exchange-asset' ? isCatalogLoading || isExchangeAssetFetching : isAssetLevelFetching);

  const isError =
    isPriceError || (scope === 'exchange-asset' ? isCatalogError || isExchangeAssetError : isAssetLevelError);

  // Any active query stopping at the pagination cap means data is incomplete — timeseries drops the
  // oldest points (paging_from 'end'), the catalog drops exchanges. Skipped queries have undefined
  // currentData, so a flat OR only reflects active ones.
  const isTruncated =
    isCatalogTruncated ||
    exchangeAssetData?.truncated === true ||
    assetLevelData?.truncated === true ||
    priceData?.truncated === true;

  const fundingStartTime = useMemo(() => earliestTimestamp(fundingRateSeries), [fundingRateSeries]);

  return {
    fundingRateSeries,
    priceSeries,
    availableExchanges,
    selectedExchanges: effectiveExchanges,
    fundingStartTime,
    isFetching,
    isError,
    isPriceForbidden,
    isTruncated,
  };
}
